// Merchant quote-requests inbox. Lists requests addressed to this merchant OR
// open requests in their city. "Build quote" opens the quote builder, where the
// merchant prices the cart and sends a quote to the customer.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Loader2, MapPin, Clock, ArrowRight } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState, StatusPill } from '../../components/shared/primitives';
import { MERCHANT_NAV } from '../_navs';
import { requests, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const QUOTE_PENDING_STATUSES = ['submitted', 'quote_pending'];

export function MerchantQuoteRequestsPage() {
  // Pull EVERYTHING the merchant can see, then filter client-side.
  const q = useQuery({ queryKey: ['merchant', 'incoming'], queryFn: () => requests.list() });

  const inbox = useMemo(
    () => (q.data ?? []).filter((r) => QUOTE_PENDING_STATUSES.includes(r.status)),
    [q.data],
  );

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="inbox"
      title="Quote requests"
      subtitle="Customers waiting for your price."
    >
      <div className="space-y-5">
        {q.isLoading && (
          <Card><div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading inbox…</div></Card>
        )}

        {!q.isLoading && inbox.length === 0 && (
          <Card>
            <EmptyState
              icon={Inbox}
              title="Inbox is empty"
              body="Once a customer submits a cart for a recipient in your city — or addresses one specifically to you — it appears here."
            />
          </Card>
        )}

        {inbox.length > 0 && (
          <Card padding="p-0">
            <div className="p-5 border-b border-slate-100">
              <SectionHeader
                title={`${inbox.length} pending`}
                subtitle="Pre-built carts ready for your quote — click to price the items and send a quote."
              />
            </div>
            <>
              {/* Mobile: stacked cards */}
              <div className="sm:hidden divide-y divide-slate-100">
                {inbox.map((r) => <InboxCard key={r.id} r={r} />)}
              </div>
              {/* sm+: table */}
              <table className="hidden sm:table text-[13px] w-full">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr>
                    <th className="text-left py-2.5 px-5 font-medium">Request</th>
                    <th className="text-left py-2.5 px-3 font-medium">Recipient</th>
                    <th className="text-left py-2.5 px-3 font-medium">City</th>
                    <th className="text-left py-2.5 px-3 font-medium">Items</th>
                    <th className="text-right py-2.5 px-3 font-medium">Cart (HTG)</th>
                    <th className="text-right py-2.5 px-5 font-medium">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inbox.map((r) => <InboxRow key={r.id} r={r} />)}
                </tbody>
              </table>
            </>
          </Card>
        )}
      </div>
    </PortalShell>
  );
}

function ageOf(created: string) {
  const m = Math.max(0, Math.floor((Date.now() - new Date(created).getTime()) / 60000));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`;
}

function InboxCard({ r }: { r: ServiceRequestWithItems }) {
  return (
    <Link to={`/merchant/quote-requests/${r.id}`} className="block px-4 py-3 active:bg-slate-50">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[13px] text-slate-700">{r.recipient?.full_name ?? '—'} <span className="text-slate-400">· {r.category?.label ?? ''}</span></p>
      <div className="mt-1 flex items-center justify-between text-[12px] text-slate-500">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city?.name ?? '—'} · {r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</span>
        <span className="font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : '—'}</span>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ageOf(r.created_at)} ago</span>
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600">Build quote <ArrowRight className="w-3.5 h-3.5" /></span>
      </div>
    </Link>
  );
}

function InboxRow({ r }: { r: ServiceRequestWithItems }) {
  const age = ageOf(r.created_at);
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <p className="font-mono text-cyan-700 text-[12.5px]">REQ-{String(r.id).padStart(4, '0')}</p>
        <p className="text-[12px] text-slate-600 mt-0.5">{r.category?.label ?? '—'}</p>
        <p className="mt-1"><StatusPill status={r.status} /></p>
      </td>
      <td className="py-3 px-3 text-slate-700">{r.recipient?.full_name ?? '—'}</td>
      <td className="py-3 px-3 text-slate-700 inline-flex items-center gap-1 mt-3">
        <MapPin className="w-3 h-3 text-slate-400" /> {r.city?.name ?? '—'}
      </td>
      <td className="py-3 px-3 text-slate-700">{r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</td>
      <td className="py-3 px-3 text-right font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : <span className="text-slate-400">—</span>}</td>
      <td className="py-3 px-5 text-right">
        <p className="text-[11px] text-slate-500 inline-flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {age} ago</p>
        <Link to={`/merchant/quote-requests/${r.id}`} className="mt-1 inline-flex items-center gap-1 px-2.5 py-1 rounded bg-brand-600 text-white text-[11.5px] font-semibold hover:bg-brand-700">
          Build quote <ArrowRight className="w-3 h-3" />
        </Link>
      </td>
    </tr>
  );
}
